import React, { useEffect, useRef, useState } from 'react';
import uuid from 'uuid-random';
import { pick } from 'ramda';

import { Portal } from 'js/app/components/system/Portal/Portal';
import { UnregisteredPortlet } from 'js/components/system/Portlet/components/UnregisteredPortlet';
import { AdminComponent } from 'js/app/components/system/Portal/components/PortalPlace/components/AdminComponent';

import { dispatcher } from 'dispatcher';
import { useIsAdmin } from 'js/hooks/user';
import { usePageSelector } from 'js/store/hooks';
import { componentService } from 'js/services/component.service';
import { parseStrInArray } from '@interlayer/js/app/utils/common.util';
import { ADMIN_RESERVE_BLOCK } from 'js/app/components/system/Portal/constants';

import style from './PortalPortlet.scss';

const findAdditionalConfigByDefault = ({ id, overwriteConfig }) => {
    return overwriteConfig.find(({ dataId: dataIds }) => parseStrInArray(dataIds).find(currentId => id === currentId)) || {};
};

export const PortalPortlet = ({
    portletConfig,
    findAdditionalConfig = findAdditionalConfigByDefault
}) => {
    const isAdmin = useIsAdmin();
    const id = useRef(ADMIN_RESERVE_BLOCK);
    const key = useRef(uuid());
    const [overrideConfig, setOverrideConfig] = useState({});
    const { portlets, pageLayoutId } = usePageSelector(pick(['portlets', 'pageLayoutId']));
    const PortletComponent = componentService.getPageComponents()[portletConfig.portletType]
        || (isAdmin ? UnregisteredPortlet : null);

    useEffect(() => {
        const checkId = (idForCheck) => {
            if (idForCheck !== portletConfig.idPortal) {
                return;
            }

            const parentElem = document.querySelector(`#application #${idForCheck}`);
            const dataIdAttribute = parentElem?.dataset.id;

            setOverrideConfig(dataIdAttribute && portletConfig.overwriteConfig?.length
                ? findAdditionalConfig({
                    id: dataIdAttribute,
                    overwriteConfig: portletConfig.overwriteConfig
                })
                : {}
            );

            const newId = parentElem ? idForCheck : ADMIN_RESERVE_BLOCK;

            if (newId !== id.current) {
                key.current = uuid();
            }

            id.current = newId;
        };

        checkId(portletConfig.idPortal);

        dispatcher.listen('portalPlace:update', checkId);

        return () => {
            dispatcher.removeListener('portalPlace:update', checkId);
        };
    }, [
        findAdditionalConfig, portletConfig.idPortal,
        portletConfig.overwriteConfig, portletConfig.segmentation
    ]);

    useEffect(() => {
        const moveToAdminBlock = (idForCheck) => {
            if (idForCheck !== portletConfig.idPortal) {
                return;
            }

            id.current = ADMIN_RESERVE_BLOCK;
        };

        dispatcher.listen('portalPlace:removed', moveToAdminBlock);

        return () => {
            dispatcher.removeListener('portalPlace:removed', moveToAdminBlock);
        };
    }, [isAdmin, portletConfig.idPortal]);

    useEffect(() => {
        if (isAdmin && window.initPageAdminUI) {
            window.initPageAdminUI({ portlets, pageLayoutId });
        }
    }, [isAdmin, pageLayoutId, portlets]);

    return (
        <Portal id={id.current} key={key.current}>
            {isAdmin
                ? (
                    <div
                        id={`p_p_id_${portletConfig.portletId}`}
                        data-portlet-id={portletConfig.portletId}
                        data-portlet-type={portletConfig.portletType}
                        data-react-portlet-id={portletConfig.reactPortletId}
                        className={style.container}
                    >
                        <PortletComponent
                            key={portletConfig.reactPortletId}
                            config={{ ...portletConfig, ...overrideConfig }}
                        />
                        <div className={style.describe}>
                            <AdminComponent id={portletConfig.idPortal} />
                            {portletConfig.describePlace && id.current === ADMIN_RESERVE_BLOCK && (
                                <div className={style.describeText}>{portletConfig.describePlace}</div>
                            )}
                        </div>
                    </div>
                )
                : (
                    <PortletComponent
                        key={portletConfig.reactPortletId}
                        config={{ ...portletConfig, ...overrideConfig }}
                    />
                )
            }
        </Portal>
    );
};
